import type { Bootstrap, CartLine, ConfigState, PriceResponse } from './types';

/**
 * The server is the only pricing implementation (DECISIONS D8).
 *
 * There is deliberately no client-side mirror price. The spec asked for one plus a
 * diagnostics endpoint to log the drift it would guarantee; both were dropped. What
 * the customer sees is what the server computed.
 */

/** Serialises the client state into the wire shape the REST layer expects. */
export function toWireConfig(config: ConfigState, profileVersion: number): Record<string, unknown> {
	return {
		profile: config.profileSlug,
		profile_version: profileVersion,
		segments_mm: config.segments,
		angles_deg: config.angles,
		directions: config.directions,
		length_mm: config.lengthMm,
		qty: config.qty,
		material_id: config.materialId,
		color_front_id: config.colorId,
		notes: config.notes,
	};
}

/**
 * Asks the server what a configuration costs.
 *
 * No nonce (SPEC_DEFECTS F-14): this page can be served from Breeze or Varnish with
 * a stale nonce inlined, and a 403 on every price lookup would be a dead
 * configurator. The endpoint has no side effects and is rate limited instead.
 */
export async function fetchPrice(
	boot: Bootstrap,
	config: ConfigState,
	profileVersion: number,
	signal: AbortSignal
): Promise<PriceResponse> {
	const response = await fetch(`${boot.restUrl}price`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ config: toWireConfig(config, profileVersion) }),
		signal,
	});

	if (!response.ok && response.status !== 200) {
		const body = (await response.json().catch(() => null)) as { message?: string } | null;

		return {
			valid: false,
			errors: [
				{
					field: 'general',
					code: 'server',
					message: body?.message ?? boot.i18n.priceFailed,
				},
			],
		};
	}

	return (await response.json()) as PriceResponse;
}

/**
 * The nonce to send with the next cart write.
 *
 * Starts as the one baked into the page and is replaced the first time that one turns
 * out to be dead, so a visitor pays the extra round trip once rather than on every add.
 */
let cartNonce: string | null = null;

/**
 * Fetches a nonce minted in this request.
 *
 * The endpoint lives under sd/v1, which the plugin marks no-store, so the answer cannot
 * itself come from a cache.
 */
async function freshNonce(boot: Bootstrap): Promise<string | null> {
	try {
		const response = await fetch(`${boot.restUrl}nonce`, {
			headers: { Accept: 'application/json' },
			cache: 'no-store',
		});

		if (!response.ok) {
			return null;
		}

		const body = (await response.json()) as { nonce?: string };

		return typeof body.nonce === 'string' && body.nonce !== '' ? body.nonce : null;
	} catch {
		return null;
	}
}

/** One attempt at the cart write, with whatever nonce we currently believe in. */
async function postToCart(
	boot: Bootstrap,
	config: ConfigState,
	profileVersion: number,
	nonce: string
): Promise<{
	response: Response;
	body: { message?: string; cart_url?: string; code?: string } | null;
}> {
	const response = await fetch(`${boot.restUrl}cart`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce },
		body: JSON.stringify({ config: toWireConfig(config, profileVersion) }),
	});

	const body = (await response.json().catch(() => null)) as {
		message?: string;
		cart_url?: string;
		code?: string;
	} | null;

	return { response, body };
}

/**
 * Adds the configured part to the cart, refreshing a dead nonce once if it has to.
 *
 * SPEC_DEFECTS F-14, the half that was never built. This route carries a nonce because
 * it has side effects and because WordPress treats a REST request with NO nonce as
 * user 0 — `rest_cookie_check_errors` calls `wp_set_current_user( 0 )` — so simply
 * dropping it would put a logged-in customer's part in an anonymous session's basket.
 * Refreshing is therefore the only safe half of the two the owner offered.
 *
 * The token is minted when the PAGE is generated and lives about 24 hours. The
 * configurator page is deliberately cacheable (F-13), so every cached copy eventually
 * outlives the nonce inside it, and from that moment every visitor served that copy gets
 * „Slapukų patikra nepavyko" until somebody purges. A scheduled outage, not a flake.
 *
 * ONE RETRY, NEVER A LOOP. A second failure is a real refusal — a signed-out session, a
 * genuinely broken cookie — and retrying it forever would turn a clear error into a
 * hang.
 */
export async function addToCart(
	boot: Bootstrap,
	config: ConfigState,
	profileVersion: number
): Promise<{ ok: boolean; message: string; cartUrl: string | undefined }> {
	let attempt = await postToCart(boot, config, profileVersion, cartNonce ?? boot.cartNonce);

	if (attempt.response.status === 403 && attempt.body?.code === 'rest_cookie_invalid_nonce') {
		const fresh = await freshNonce(boot);

		if (fresh !== null) {
			cartNonce = fresh;
			attempt = await postToCart(boot, config, profileVersion, fresh);
		}
	}

	const { response, body } = attempt;

	return {
		ok: response.ok,
		message: body?.message ?? (response.ok ? boot.i18n.addedToCart : boot.i18n.addFailed),
		cartUrl: body?.cart_url,
	};
}

interface StoreApiItemData {
	readonly key?: string;
	readonly name?: string;
	readonly value?: string;
	readonly display?: string;
}

interface StoreApiItem {
	readonly key: string;
	readonly name: string;
	readonly quantity: number;
	readonly totals?: { readonly line_total?: string; readonly line_total_tax?: string };
	readonly item_data?: StoreApiItemData[];
	readonly extensions?: { readonly 'sd-configurator'?: { readonly is_configurator?: boolean } };
}

/**
 * Reads the current basket, for the running summary beside the configurator.
 *
 * The Store API cart endpoint is a plain GET and needs no nonce, so this works on
 * a page served from Breeze or Varnish. Only configurator lines are listed: the
 * summary exists to confirm the parts the customer has designed, and mixing catalog
 * products into it would make it a second, worse cart.
 */
export async function fetchCart(boot: Bootstrap): Promise<{ lines: CartLine[]; cartUrl: string }> {
	const fallback = { lines: [] as CartLine[], cartUrl: boot.cartUrl };

	try {
		const response = await fetch('/wp-json/wc/store/v1/cart', {
			headers: { 'Content-Type': 'application/json' },
			credentials: 'same-origin',
		});

		if (!response.ok) {
			return fallback;
		}

		const body = (await response.json()) as { items?: StoreApiItem[] };

		const lines = (body.items ?? [])
			.filter((item) => item.extensions?.['sd-configurator']?.is_configurator === true)
			.map((item) => {
				const rows = item.item_data ?? [];
				const pick = (label: string): string =>
					rows.find((row) => row.key === label || row.name === label)?.display ?? '';

				const total = Number(item.totals?.line_total ?? '0');
				const tax = Number(item.totals?.line_total_tax ?? '0');

				return {
					key: item.key,
					name: item.name,
					// Dimensions in the order they appear on the drawing, then length.
					summary: [pick('Matmenys'), pick('Ilgis')].filter(Boolean).join(' · '),
					qty: item.quantity,
					totalCents: Math.round(total + tax),
				};
			});

		return { lines, cartUrl: boot.cartUrl };
	} catch {
		return fallback;
	}
}
